// =============================================================================
// Script Selector - 脚本选择器
// =============================================================================

import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Code, 
  Clock, 
  Tag,
  ChevronDown,
  Check,
  Info,
  Zap
} from 'lucide-react';

// 脚本类别图标映射
const CATEGORY_ICONS = {
  monitoring: '📊',
  development: '🔧',
  service: '🚀',
  automation: '🤖',
  testing: '🧪'
};

export default function ScriptSelector({ 
  selectedScript = null,
  onScriptSelect,
  terminalType = 'general',
  disabled = false,
  showDescription = true,
  compact = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [availableScripts, setAvailableScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, recommended, category

  useEffect(() => {
    fetchAvailableScripts();
  }, [terminalType]);

  const fetchAvailableScripts = async () => {
    try {
      console.log('🔍 Fetching script templates...');
      setLoading(true);

      // 获取所有脚本模板
      const response = await fetch('/api/scripts/templates', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📋 Script templates data:', data);
      
      if (data.success && data.data && data.data.templates) {
        setAvailableScripts(data.data.templates);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ Failed to fetch script templates:', error);
      // 提供默认脚本作为后备
      setAvailableScripts([
        {
          id: 'system_monitor',
          name: '系统监控',
          description: '监控系统资源使用情况',
          category: 'monitoring',
          terminalType: 'monitoring',
          autoExecute: true,
          expectedDuration: 30000,
          tags: ['monitoring', 'system'],
          stepCount: 5
        },
        {
          id: 'project_build',
          name: '项目构建',
          description: '自动构建Node.js项目',
          category: 'development',
          terminalType: 'development',
          autoExecute: true,
          expectedDuration: 300000,
          tags: ['development', 'build'],
          stepCount: 6
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleScriptSelect = (script) => {
    if (onScriptSelect) {
      onScriptSelect(script);
    }
    setIsOpen(false);
  };

  const getFilteredScripts = () => {
    if (filter === 'recommended') {
      return availableScripts.filter(script => script.terminalType === terminalType);
    }
    if (filter !== 'all') {
      return availableScripts.filter(script => script.category === filter);
    }
    return availableScripts;
  };

  const formatDuration = (ms) => {
    if (!ms || ms === 0) return '持续运行';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}小时${minutes % 60}分钟`;
    if (minutes > 0) return `${minutes}分钟${seconds % 60}秒`;
    return `${seconds}秒`;
  };

  const selectedScriptInfo = availableScripts.find(s => s.id === selectedScript?.id);
  const filteredScripts = getFilteredScripts();
  const categories = [...new Set(availableScripts.map(s => s.category))];

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
        <span>加载脚本模板...</span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={`flex items-center space-x-2 px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
            selectedScript 
              ? 'bg-blue-50 text-blue-700 border-blue-200' 
              : 'bg-gray-50 text-gray-700 border-gray-200'
          } ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-80 cursor-pointer'
          }`}
        >
          <Play size={16} />
          <span>{selectedScript?.name || '选择脚本'}</span>
          <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
            <div className="p-3 border-b">
              <div className="flex space-x-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-2 py-1 text-xs rounded ${filter === 'all' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  全部
                </button>
                <button
                  onClick={() => setFilter('recommended')}
                  className={`px-2 py-1 text-xs rounded ${filter === 'recommended' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  推荐
                </button>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setFilter(category)}
                    className={`px-2 py-1 text-xs rounded ${filter === category ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {CATEGORY_ICONS[category]} {category}
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filteredScripts.map((script) => {
                const isSelected = script.id === selectedScript?.id;
                
                return (
                  <button
                    key={script.id}
                    onClick={() => handleScriptSelect(script)}
                    className={`w-full flex items-start space-x-3 px-3 py-3 text-left hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="text-lg">{CATEGORY_ICONS[script.category] || '📋'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                          {script.name}
                        </span>
                        {isSelected && <Check size={14} className="text-blue-600" />}
                        {script.autoExecute && <Zap size={12} className="text-yellow-500" title="自动执行" />}
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{script.description}</p>
                      <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Clock size={10} />
                          <span>{formatDuration(script.expectedDuration)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Code size={10} />
                          <span>{script.stepCount} 步骤</span>
                        </span>
                      </div>
                      {script.tags && script.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {script.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="inline-block px-1 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {filteredScripts.length === 0 && (
              <div className="p-4 text-center text-gray-500 text-sm">
                没有找到匹配的脚本
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium text-gray-700">自动执行脚本</label>
        <Info size={14} className="text-gray-400" title="选择在终端创建时自动执行的脚本" />
      </div>

      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg text-left transition-colors ${
            disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white hover:bg-gray-50 cursor-pointer'
          } ${
            selectedScript 
              ? 'border-blue-200 bg-blue-50' 
              : 'border-gray-200'
          }`}
        >
          <div className="flex items-center space-x-3">
            <div className="text-xl">{selectedScript ? CATEGORY_ICONS[selectedScript.category] || '📋' : '📋'}</div>
            <div>
              <div className="font-medium">{selectedScript?.name || '选择脚本（可选）'}</div>
              {showDescription && selectedScript?.description && (
                <div className="text-sm text-gray-500">{selectedScript.description}</div>
              )}
            </div>
          </div>
          <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="p-4 border-b">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 text-sm rounded-md ${filter === 'all' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  全部脚本
                </button>
                <button
                  onClick={() => setFilter('recommended')}
                  className={`px-3 py-1 text-sm rounded-md ${filter === 'recommended' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  推荐脚本
                </button>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setFilter(category)}
                    className={`px-3 py-1 text-sm rounded-md ${filter === category ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {CATEGORY_ICONS[category]} {category}
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <button
                onClick={() => handleScriptSelect(null)}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 ${
                  !selectedScript ? 'bg-blue-50' : ''
                }`}
              >
                <div className="text-xl">🚫</div>
                <div>
                  <div className={`font-medium ${!selectedScript ? 'text-blue-700' : 'text-gray-900'}`}>
                    不执行脚本
                  </div>
                  <div className="text-sm text-gray-500">创建终端后不自动执行任何脚本</div>
                </div>
                {!selectedScript && <Check size={16} className="text-blue-600 ml-auto" />}
              </button>
              {filteredScripts.map((script) => {
                const isSelected = script.id === selectedScript?.id;
                
                return (
                  <button
                    key={script.id}
                    onClick={() => handleScriptSelect(script)}
                    className={`w-full flex items-start space-x-3 px-4 py-4 text-left hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="text-xl">{CATEGORY_ICONS[script.category] || '📋'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                          {script.name}
                        </span>
                        {isSelected && <Check size={16} className="text-blue-600" />}
                        {script.autoExecute && <Zap size={14} className="text-yellow-500" title="自动执行" />}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{script.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Clock size={12} />
                          <span>预计时长: {formatDuration(script.expectedDuration)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Code size={12} />
                          <span>{script.stepCount} 个步骤</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Tag size={12} />
                          <span>{script.category}</span>
                        </span>
                      </div>
                      {script.tags && script.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {script.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {filteredScripts.length === 0 && (
              <div className="p-6 text-center text-gray-500">
                没有找到匹配的脚本模板
              </div>
            )}
          </div>
        )}
      </div>

      {selectedScript && showDescription && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-700">
            <div className="font-medium mb-1">脚本详情:</div>
            <div className="space-y-1">
              <div>• 类别: {selectedScript.category}</div>
              <div>• 预计时长: {formatDuration(selectedScript.expectedDuration)}</div>
              <div>• 步骤数: {selectedScript.stepCount}</div>
              <div>• 自动执行: {selectedScript.autoExecute ? '是' : '否'}</div>
              {selectedScript.tags && (
                <div>• 标签: {selectedScript.tags.join(', ')}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
